import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  PublishOutlined as PublishOutlinedIcon,
  RefreshOutlined as RefreshOutlinedIcon,
  SearchOutlined as SearchOutlinedIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import type { ClinicDocumentCardResponse } from '../types';

import { ClinicDocumentListItem } from './ClinicDocumentListItem';

const DOC_KIND_OPTIONS = [
  { value: 'all', label: 'All kinds' },
  { value: 'contract', label: 'Contract' },
  { value: 'fee_schedule', label: 'Fee schedule' },
  { value: 'policy', label: 'Policy' },
  { value: 'manual', label: 'Manual' },
  { value: 'form', label: 'Form' },
  { value: 'credentialing', label: 'Credentialing' },
  { value: 'other', label: 'Other' },
] as const;

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'uploaded', label: 'Uploaded' },
  { value: 'metadata_ready', label: 'Metadata ready' },
  { value: 'processing', label: 'Processing' },
  { value: 'processed', label: 'Processed' },
  { value: 'failed', label: 'Failed' },
] as const;

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface ClinicDocumentsSidebarProps {
  documents: ClinicDocumentCardResponse[];
  selectedDocumentId: string | null;
  isLoading: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
  total: number;
  offset: number;
  pageSize: number;
  searchQuery: string;
  docKindFilter: string;
  statusFilter: string;
  selectedFiles: File[];
  isUploading: boolean;
  onSelectDocument: (documentId: string) => void;
  onStatusChange: (documentId: string, nextStatus: string, nextTitle: string | null) => void;
  onSearchChange: (value: string) => void;
  onDocKindFilterChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onClearFilters: () => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onFileSelection: (event: ChangeEvent<HTMLInputElement>) => void;
  onUpload: () => void;
  onRefresh: () => void;
}

export function ClinicDocumentsSidebar({
  documents,
  selectedDocumentId,
  isLoading,
  isFetching,
  hasNextPage,
  total,
  offset,
  pageSize,
  searchQuery,
  docKindFilter,
  statusFilter,
  selectedFiles,
  isUploading,
  onSelectDocument,
  onStatusChange,
  onSearchChange,
  onDocKindFilterChange,
  onStatusFilterChange,
  onClearFilters,
  onNextPage,
  onPreviousPage,
  onFileSelection,
  onUpload,
  onRefresh,
}: ClinicDocumentsSidebarProps) {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [showBottomGradient, setShowBottomGradient] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const hasActiveFilters =
    !!searchQuery || docKindFilter !== 'all' || statusFilter !== 'all';

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.floor(offset / pageSize) + 1;

  const checkScrollOverflow = useCallback(() => {
    if (!listRef.current) return;
    const el = listRef.current;
    const hasOverflow = el.scrollHeight > el.clientHeight;
    const isScrolledToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 5;
    setShowBottomGradient(hasOverflow && !isScrolledToBottom);
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const handleScroll = () => checkScrollOverflow();
    el.addEventListener('scroll', handleScroll, { passive: true });

    const resizeObserver = new ResizeObserver(() => checkScrollOverflow());
    resizeObserver.observe(el);

    const rafId = requestAnimationFrame(() => {
      checkScrollOverflow();
      setTimeout(checkScrollOverflow, 100);
    });

    return () => {
      cancelAnimationFrame(rafId);
      el.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [checkScrollOverflow, documents, isLoading]);

  return (
    <Box
      component="aside"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRight: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2, pt: 2, pb: 1.5, flexShrink: 0 }}>
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Documents
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isFetching ? 'Refreshing...' : `${total} total`}
            </Typography>
          </Box>
          <Tooltip title="Refresh list">
            <IconButton size="small" onClick={onRefresh} aria-label="Refresh document list">
              <RefreshOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Divider />

      {/* Upload section */}
      <Box sx={{ px: 2, py: 1, flexShrink: 0 }}>
        <Button
          fullWidth
          size="small"
          variant={uploadOpen ? 'contained' : 'outlined'}
          startIcon={<PublishOutlinedIcon />}
          endIcon={uploadOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setUploadOpen(prev => !prev)}
          sx={{ justifyContent: 'flex-start' }}
        >
          Upload PDFs
        </Button>
        <Collapse in={uploadOpen}>
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            <Button
              component="label"
              size="small"
              variant="outlined"
              startIcon={<PublishOutlinedIcon />}
              sx={{ justifyContent: 'flex-start' }}
            >
              Choose files
              <input
                hidden
                multiple
                accept="application/pdf,.pdf"
                type="file"
                onChange={onFileSelection}
              />
            </Button>

            {selectedFiles.length > 0 && (
              <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
                {selectedFiles.map(file => (
                  <Chip
                    key={`${file.name}-${file.size}`}
                    size="small"
                    label={`${file.name} · ${formatBytes(file.size)}`}
                  />
                ))}
              </Stack>
            )}

            <Button
              variant="contained"
              size="small"
              onClick={onUpload}
              disabled={isUploading || !selectedFiles.length}
              startIcon={
                isUploading ? <CircularProgress size={14} color="inherit" /> : undefined
              }
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </Button>
          </Stack>
        </Collapse>
      </Box>

      <Divider />

      {/* Search */}
      <Box sx={{ px: 2, py: 1, flexShrink: 0 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search documents..."
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
         slotProps={{ input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          } }}/>
      </Box>

      <Divider />

      {/* Filters */}
      <Box sx={{ px: 2, py: 1, flexShrink: 0 }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={0.75}>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel id="sidebar-kind-label">Kind</InputLabel>
              <Select
                labelId="sidebar-kind-label"
                label="Kind"
                value={docKindFilter}
                onChange={e => onDocKindFilterChange(e.target.value)}
              >
                {DOC_KIND_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel id="sidebar-status-label">Status</InputLabel>
              <Select
                labelId="sidebar-status-label"
                label="Status"
                value={statusFilter}
                onChange={e => onStatusFilterChange(e.target.value)}
              >
                {STATUS_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {hasActiveFilters && (
            <Button size="small" onClick={onClearFilters} sx={{ alignSelf: 'flex-end' }}>
              Clear filters
            </Button>
          )}
        </Stack>
      </Box>

      <Divider />

      {/* Document list */}
      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        <Box
          ref={listRef}
          sx={{ height: '100%', overflowY: 'auto', px: 1.5, py: 1 }}
        >
          {isLoading ? (
            <Stack spacing={1}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} variant="rounded"  sx={{ height: 56 }}/>
              ))}
            </Stack>
          ) : documents.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              No documents match the current filters.
            </Alert>
          ) : (
            <Stack spacing={0.75}>
              {documents.map(doc => (
                <ClinicDocumentListItem
                  key={doc.clinic_document_id}
                  document={doc}
                  isSelected={selectedDocumentId === doc.clinic_document_id}
                  onClick={() => onSelectDocument(doc.clinic_document_id)}
                  onStatusChange={onStatusChange}
                />
              ))}
            </Stack>
          )}

          {/* Pagination */}
          {documents.length > 0 && (
            <Stack
              direction="row"
              sx={{justifyContent: "space-between", alignItems: "center",  mt: 1.5, pt: 1, borderTop: theme => `1px solid ${theme.palette.divider}` }}
            >
              <Typography variant="caption" color="text.secondary">
                Page {currentPage} of {totalPages}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Button
                  size="small"
                  variant="text"
                  onClick={onPreviousPage}
                  disabled={offset === 0}
                >
                  Prev
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={onNextPage}
                  disabled={!hasNextPage}
                >
                  Next
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>

        {/* Bottom gradient fade */}
        {showBottomGradient && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 40,
              background: 'linear-gradient(transparent, rgba(255,255,255,0.95))',
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
    </Box>
  );
}
